import Component from '@glimmer/component'
import type { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import { runTask } from 'ember-lifeline'
import PopoverStandardDropdown from '@blakeelearning/popovers/components/popover/dropdowns/standard'
import { ThemedDropdownContent } from 'district-ui-client/components/themed-dropdown'
import { ThemedMenu, ThemedMenuGroup, ThemedMenuItemLink } from 'district-ui-client/components/themed-menu'
import { FeatureNotificationId } from 'district-ui-client/services/feature-notification'
import { FeatureNotification } from 'district-ui-client/helpers/feature-notification'
import { SiteHeaderNavButton } from 'district-ui-client/components/site-header-nav-button'
import { PhosphorIcon } from 'district-ui-client/components/phosphor-icon'
import { NotificationDot } from 'district-ui-client/components/notification-dot'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { or } from 'ember-truth-helpers'
import { Lozenge } from 'district-ui-client/components/lozenge'
import t from 'ember-intl/helpers/t'
import { service } from '@ember/service'
import type ExternalRosteringService from 'district-ui-client/services/external-rostering'
import type ImpressionService from 'district-ui-client/services/impression'
import type SessionService from 'district-ui-client/services/session'

interface IntegrationsSiteHeaderSignature {
  Element: HTMLElement
  Args: {
    subscriptionType?: SubscriptionType
  }
}

export class IntegrationsSiteHeader extends Component<IntegrationsSiteHeaderSignature> {
  @service externalRostering!: ExternalRosteringService

  @service session!: SessionService

  @service impression!: ImpressionService

  // We need the LinkTo to handle the event first, while the element is still rendered, then close the dropdown.
  // Otherwise, the page hard refreshes. see: https://github.com/cibernox/ember-basic-dropdown/issues/528
  delayClose = (closeAction: () => unknown) => {
    runTask(this, () => {
      closeAction()
      // This is to prevent the button from staying focused after the dropdown closes
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    })
  }

  manageUrlClicked = (closeAction: () => unknown) => {
    this.externalRostering.logManageUrlClick()
    this.delayClose(closeAction)
  }

  usageSummaryUrlClicked = (closeAction: () => unknown) => {
    this.externalRostering.logUsageSummaryUrlClick()
    this.delayClose(closeAction)
  }

  get isVisible() {
    return !!this.usageSummaryUrl || !!this.manageUrl
  }

  // link to disco role CLIP or IP integration management/issues page
  get manageUrl() {
    return this.externalRostering.manageUrl(this.args.subscriptionType)
  }

  // link to disco role Roster integration status/usage summary page
  get usageSummaryUrl() {
    return this.externalRostering.usageSummaryUrl(this.args.subscriptionType)
  }

  <template>
    {{#if this.isVisible}}
      <PopoverStandardDropdown as |dropdown status actions|>
        <SiteHeaderNavButton
          data-test-external-rostering-trigger
          @isOpen={{status.isOpen}}
          {{dropdown.makeTrigger}}
          ...attributes
        >
          <span class="relative flex items-center gap-2">
            <PhosphorIcon @icon="phosphor-link-bold" />
            <span>{{this.externalRostering.displayName}}</span>
            <PhosphorIcon @icon="phosphor-caret-down-bold" />
            {{#if
              (or
                (FeatureNotification FeatureNotificationId.ManageIntegration)
                (FeatureNotification FeatureNotificationId.IntegrationUsageSummary)
              )
            }}
              <NotificationDot data-test-notification-dot class="absolute -right-1.5 -top-0.5" />
            {{/if}}
          </span>
        </SiteHeaderNavButton>
        <dropdown.content>
          {{! The button trigger box is bigger than the button itself.
        The negative margin here counters the my-3 + h-1 below the button, to end up with the intended 1 unit gap }}
          <ThemedDropdownContent class="!-mt-3" @constrainWidth={{true}}>
            <ThemedMenu>
              <ThemedMenuGroup>
                {{! "Usage summary" link to Roster  }}
                {{#if this.usageSummaryUrl}}
                  <ThemedMenuItemLink
                    class="flex items-center gap-2 text-neutral-300"
                    @href={{this.usageSummaryUrl}}
                    {{on "click" (fn this.usageSummaryUrlClicked actions.close)}}
                    data-test-external-rostering-status-for={{this.externalRostering.source}}
                  >
                    <PhosphorIcon @icon="phosphor-user-list-fill" />
                    <span>
                      {{t "components.homePage.externalRosteringTile.title"}}
                    </span>
                    {{#if (FeatureNotification FeatureNotificationId.IntegrationUsageSummary)}}
                      <Lozenge class="shrink-0 grow-0 px-2">{{t "new"}}</Lozenge>
                    {{/if}}
                  </ThemedMenuItemLink>
                {{/if}}
                {{! "Manage" link to CLIP or IP }}
                {{#if this.manageUrl}}
                  <ThemedMenuItemLink
                    class="flex items-center gap-2 text-neutral-300"
                    @href={{this.manageUrl}}
                    {{on "click" (fn this.manageUrlClicked actions.close)}}
                    data-test-external-rostering-manage-for={{this.externalRostering.source}}
                  >
                    <PhosphorIcon @icon="phosphor-users-fill" />
                    <span class="flex-grow">
                      {{t "components.homePage.externalRosteringReviewTile.title"}}
                    </span>
                    {{#if (FeatureNotification FeatureNotificationId.ManageIntegration)}}
                      <Lozenge class="shrink-0 grow-0 px-2">{{t "new"}}</Lozenge>
                    {{/if}}
                  </ThemedMenuItemLink>
                {{/if}}
              </ThemedMenuGroup>
            </ThemedMenu>
          </ThemedDropdownContent>
        </dropdown.content>
      </PopoverStandardDropdown>
    {{/if}}
  </template>
}
